/**
 * CompanyEventPopup — app-level modal showing published company events whose window
 * covers today (GET /api/company-events/active). Mounted in rootRoute next to
 * ScheduleRegisterForcedPopup. Dismissed event ids persist in localStorage so each
 * event pops up once per browser.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { authClient } from '../lib/auth-client'
import { getActiveCompanyEvents } from '../lib/api-client'
import { Button } from '../components/ui/button'
import { useT } from '../i18n/language-provider'

const SEEN_KEY = 'seen-company-events'

function readSeen(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) ?? '[]') as string[]) } catch { return new Set() }
}

function markSeen(id: string): void {
  const seen = readSeen()
  seen.add(id)
  // Keep the list bounded — old event ids never come back anyway.
  localStorage.setItem(SEEN_KEY, JSON.stringify([...seen].slice(-100)))
}

export function CompanyEventPopup() {
  const t = useT()
  const { data: session, isPending } = authClient.useSession()
  const [, setTick] = useState(0) // re-render after markSeen

  const enabled = !!session && !isPending
  const { data: events } = useQuery({
    queryKey: ['company-events-active'],
    queryFn: async () => {
      const res = await getActiveCompanyEvents()
      if (!res.success) throw new Error(res.error.message)
      return res.data
    },
    enabled,
    staleTime: 10 * 60 * 1000,
  })

  if (!enabled || !events || events.length === 0) return null
  const seen = readSeen()
  const current = events.find((e) => !seen.has(e.id))
  if (!current) return null

  return (
    <div id="company-event-popup" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border border-border bg-surface p-6 shadow-xl">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-primary">{current.header || t('event.popupTag')}</p>
        <h2 className="mb-2 text-lg font-semibold text-fg">{current.title}</h2>
        <p className="mb-1 text-xs text-muted">{current.startDate} → {current.endDate}</p>
        {current.body && <p className="mb-4 whitespace-pre-wrap text-sm text-fg-soft">{current.body}</p>}
        <div className="flex justify-end">
          <Button id="company-event-popup-ok-btn" onClick={() => { markSeen(current.id); setTick((n) => n + 1) }}>
            {t('common.ok')}
          </Button>
        </div>
      </div>
    </div>
  )
}
